import { Component, Input, OnDestroy, OnInit, inject } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TinaAddressResult } from "@modules/account/models/tina-location-result.model";
import { TinaSearchResult } from "@modules/account/models/tina-search-result.model";
import { TinaStreetResult } from "@modules/account/models/tina-street-result.model";
import { TinaMunicipalityResult } from "@modules/account/models/tina-municipality-result.model";
import { CountryModel } from "@modules/account/models/country.model";
import { TinaLocationService } from "@app/services/tina-location.service";
import { LocationService } from "@app/services/location.service";
import { Observable, Subject, of } from "rxjs";
import { takeUntil, catchError, switchMap, debounceTime } from "rxjs/operators";
import { TelenetIconComponent } from "../../../shared/telenet-icons/telenet-icon.component";
import { FormsModule } from "@angular/forms";
import { SearchAutocompleteComponent } from "../../../shared/search-autocomplete/component/search-autocomplete.component";
import { HighlightSearchDirective } from "../../../shared/search-autocomplete/helpers/highlight-search.directive";
import { AutcompleteLabelTemplateDirective, AutcompleteOptionTemplateDirective } from "../../../shared/search-autocomplete/helpers/autocomplete-templates.directive";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
   selector: "my-cloud-tina-address-search-modal",
   templateUrl: "./tina-address-search.modal.html",
   styleUrls: ["./tina-address-search.modal.scss"],
   imports: [
      TelenetIconComponent,
      FormsModule,
      SearchAutocompleteComponent,
      HighlightSearchDirective,
      AutcompleteLabelTemplateDirective,
      AutcompleteOptionTemplateDirective,
      TranslatePipe
   ]
})
export class TinaAddressSearchModalComponent implements OnInit, OnDestroy {
   modalRef = inject(NgbActiveModal);
   private tinaLocationService = inject(TinaLocationService);
   private locationService = inject(LocationService);

   private destroyed: Subject<void> = new Subject<void>();

   /** True when any Tina API call has failed — switches autocompletes to plain input fields. */
   public tinaUnavailable: boolean = false;

   /** Country code to preselect (passed in by the caller). */
   @Input() public initialCountryCode: string | undefined;

   /** TINA's municipality/street autocompletes are Belgium-only. */
   public get isBelgium(): boolean {
      return (
         !this.selectedCountry || this.selectedCountry.toUpperCase() === "BE"
      );
   }

   /** Use the TINA national autocompletes only for Belgium when the service is available. */
   public get useTinaLookup(): boolean {
      return !this.tinaUnavailable && this.isBelgium;
   }

   /**
    * Show plain text inputs (instead of the Belgium-only autocompletes) when the country is not
    * Belgium (international address search) or when Tina is unavailable (manual fallback).
    */
   public get usePlainInputs(): boolean {
      return this.tinaUnavailable || !this.isBelgium;
   }

   /** Plain country dropdown (no Tina calls) — needed for the upcoming Tina international search. */
   public countries: Array<CountryModel> = [];
   public selectedCountry: string | undefined;

   public selectedPostalCode: string | undefined;
   public selectedMunicipalityResult: TinaMunicipalityResult | undefined;
   public selectedMunicipality: string | undefined;
   public selectedStreet: TinaStreetResult | undefined;
   public streetName: string = "";
   /** Raw text the user types for the house number, e.g. "2A". */
   public houseNumberInput: string = "";
   /** Numeric part of the house number, e.g. "2". */
   public houseNumber: string = "";
   /** Non-numeric part of the house number, e.g. "A". */
   public subHouseNumber: string = "";
   public boxNumber: string = "";

   public tinaAddressResults: Array<TinaAddressResult> = [];
   public errorMessage: string = "";

   /** Debounces the address lookup while the user types a house number. */
   private houseNumberSearch: Subject<void> = new Subject<void>();

   /** Flags Tina as unavailable and shows the manual-input fallback. */
   private markTinaUnavailable(): void {
      this.tinaUnavailable = true;
      this.errorMessage = "MC_TINA_LOCATION_UNAVAILABLE";
   }

   /** Clears any previous address results and error message. */
   private resetResults(): void {
      this.tinaAddressResults = [];
      this.errorMessage = "";
   }

   public municipalitySearchFn = (
      postalCode: string
   ): Observable<Array<string>> => {
      // Only query TINA once at least a full (4-digit) postal code is entered.
      if (!postalCode || postalCode.trim().length < 4) {
         return of([]);
      }

      return this.tinaLocationService
         .searchMunicipality(this.selectedCountry, postalCode)
         .pipe(
            catchError(() => {
               this.markTinaUnavailable();
               return of([postalCode]);
            })
         );
   };

   public streetSearchFn = (
      term: string
   ): Observable<Array<TinaStreetResult>> => {
      // Only query TINA once something is typed for the street name.
      if (!term || !term.trim()) {
         return of([]);
      }

      return this.tinaLocationService
         .searchStreet(
            this.selectedCountry,
            this.selectedPostalCode ?? "",
            term
         )
         .pipe(
            catchError(() => {
               this.markTinaUnavailable();
               return of([]);
            })
         );
   };

   // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
   public ngOnInit(): void {
      this.selectedCountry = this.initialCountryCode;

      this.locationService
         .searchTranslatedCountries()
         .pipe(takeUntil(this.destroyed))
         .subscribe(countries => {
            this.countries = [...countries.filter(x => x.description)];
         });

      // Debounced address lookup: returns all addresses (incl. box variants) for the typed house number.
      this.houseNumberSearch
         .pipe(
            takeUntil(this.destroyed),
            debounceTime(400),
            switchMap(() => {
               // In manual (error) mode the user fills everything by hand and confirms — no lookup.
               if (
                  this.tinaUnavailable ||
                  !this.selectedPostalCode ||
                  !this.streetName ||
                  !this.houseNumber
               ) {
                  return of([] as Array<TinaAddressResult>);
               }

               return this.tinaLocationService
                  .searchAddress(
                     this.selectedCountry || undefined,
                     this.selectedPostalCode,
                     this.streetName,
                     this.houseNumber,
                     this.subHouseNumber || undefined,
                     this.boxNumber || undefined
                  )
                  .pipe(
                     catchError((error: { code: string; message: string }) => {
                        this.errorMessage = error?.message
                           ? `${error.message} (${error.code})`
                           : "MC_TINA_LOCATION_UNAVAILABLE";
                        return of([] as Array<TinaAddressResult>);
                     })
                  );
            })
         )
         .subscribe(results => {
            this.tinaAddressResults = results;
         });
   }

   public ngOnDestroy(): void {
      this.destroyed.next();
      this.destroyed.complete();
   }

   public get filteredAddressResults(): Array<TinaAddressResult> {
      return this.tinaAddressResults.filter(address => {
         const matchesHouseNumber =
            !this.houseNumber ||
            (address.houseNumber?.includes(this.houseNumber) ?? false);
         const matchesSubHouseNumber =
            !this.subHouseNumber ||
            (address.subHouseNumber
               ?.toLowerCase()
               .includes(this.subHouseNumber.toLowerCase()) ??
               false);
         const matchesBoxNumber =
            !this.boxNumber ||
            (address.boxNumber?.includes(this.boxNumber) ?? false);
         return matchesHouseNumber && matchesSubHouseNumber && matchesBoxNumber;
      });
   }

   public onPostalCodeSelected(postalCode: string | undefined): void {
      this.selectedMunicipality = undefined;
      this.selectedStreet = undefined;
      this.resetResults();

      this.selectedPostalCode = postalCode;
      this.tinaLocationService
         .getMunicipalityByPostalCode(this.selectedCountry, postalCode)
         .pipe(
            takeUntil(this.destroyed),
            catchError(() => {
               this.markTinaUnavailable();
               this.selectedMunicipality = postalCode;
               return of(undefined);
            })
         )
         .subscribe(result => {
            this.selectedMunicipalityResult = result;
         });
   }

   public onMunicipalitySelected(name: string | undefined): void {
      this.selectedMunicipality = name;
      this.selectedStreet = undefined;
      this.tinaAddressResults = [];
   }

   public onStreetSelected(street: TinaStreetResult | undefined): void {
      this.selectedStreet = street;
      this.streetName = street?.streetNames[0]?.name ?? "";
      this.resetResults();

      // If a house number was already entered, look up its addresses right away.
      if (street && this.houseNumber) {
         this.houseNumberSearch.next();
      }
   }

   /** Triggered while typing the house number — splits "2A" into houseNumber "2" + subHouseNumber "A", then looks up addresses. */
   public onHouseNumberChanged(value: string): void {
      this.houseNumberInput = value ?? "";

      // First run of digits = house number; the remaining non-numeric part = sub house number.
      const match = this.houseNumberInput.trim().match(/^(\d*)(.*)$/);
      this.houseNumber = (match?.[1] ?? "").trim();
      this.subHouseNumber = (match?.[2] ?? "").trim();

      this.resetResults();
      this.houseNumberSearch.next();
   }

   /** Resets the location fields when the country changes (BE vs international use different inputs). */
   public onCountryChanged(country: string | undefined): void {
      this.selectedCountry = country;
      this.selectedPostalCode = undefined;
      this.selectedMunicipalityResult = undefined;
      this.selectedMunicipality = undefined;
      this.selectedStreet = undefined;
      this.streetName = "";
      this.houseNumberInput = "";
      this.houseNumber = "";
      this.subHouseNumber = "";
      this.boxNumber = "";
      this.resetResults();
   }

   /** Triggered while typing the box number — re-runs the address lookup (box is optional). */
   public onBoxNumberChanged(value: string): void {
      this.boxNumber = value ?? "";
      this.resetResults();
      this.houseNumberSearch.next();
   }

   public selectResult(tinaLocationResult: TinaAddressResult): void {
      const result: TinaSearchResult = {
         country: this.selectedCountry ?? "",
         streetName: this.streetName,
         houseNumber: `${
            tinaLocationResult.houseNumber
         }${tinaLocationResult.subHouseNumber ?? ""}`,
         boxNumber: tinaLocationResult.boxNumber ?? "",
         postalCode: tinaLocationResult.postalCode,
         location: this.selectedMunicipality ?? "",
         tinaLocationId: tinaLocationResult.addressId
      };

      this.modalRef.close(result);
   }

   public continueWithManualInput(): void {
      const result: TinaSearchResult = {
         country: this.selectedCountry ?? "",
         streetName: this.streetName,
         houseNumber:
            this.houseNumberInput ||
            `${this.houseNumber}${this.subHouseNumber}`,
         boxNumber: this.boxNumber,
         postalCode: this.selectedPostalCode ?? "",
         location: this.selectedMunicipality ?? "",
         tinaLocationId: null
      };

      this.modalRef.close(result);
   }
}
